export const AUTH_TOKEN_KEY = 'token';


export const baseURL =
  process.env.REACT_APP_BACKEND_URL || 'https://dicom-echo-backend.cardiachealth.ai/v2';
export const frontendBaseURL = 'https://echo.cardiachealth.ai/';
export const BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'https://dicom-echo-backend.cardiachealth.ai/v2';

const _computePacsBaseURL = () => {
  // let base = 'http://localhost:8080';
  let base = 'https://pacs-dcm4chee.cardiachealth.ai';
  try {
    let overrideStr: string | null = null;
    if (typeof document !== 'undefined' && document.cookie) {
      const m = document.cookie.match(/(?:^|; )pacs_override=([^;]*)/);
      if (m) {
        overrideStr = decodeURIComponent(m[1]);
      }
    }
    if (!overrideStr && typeof localStorage !== 'undefined') {
      overrideStr = localStorage.getItem('pacs_override');
    }
    if (!overrideStr && typeof sessionStorage !== 'undefined') {
      overrideStr = sessionStorage.getItem('pacs_override');
    }
    if (overrideStr) {
      const ov = JSON.parse(overrideStr);
      if (ov && ov.ipAddress && ov.port && ov.ipAddress !== 'default' && ov.port !== '0000') {
        base = `http://${ov.ipAddress}:${ov.port}`;
      }
    } else {
      // Fallback to pacsDetails stored at login
      let detailsStr: string | null = null;
      if (typeof sessionStorage !== 'undefined') {
        detailsStr = sessionStorage.getItem('pacsDetails');
      }
      if (!detailsStr && typeof localStorage !== 'undefined') {
        detailsStr = localStorage.getItem('pacsDetails');
      }
      if (detailsStr) {
        const det = JSON.parse(detailsStr);
        if (
          det &&
          det.ipAddress &&
          det.port &&
          det.ipAddress !== 'default' &&
          det.port !== '0000'
        ) {
          base = `http://${det.ipAddress}:${det.port}`;
        }
      }
    }
  } catch (e) {}
  return base;
};
export const getPacsURL = () => _computePacsBaseURL();

const _computePacsAETitle = () => {
  let ae = 'DCM4CHEE';
  try {
    let overrideStr: string | null = null;
    if (typeof document !== 'undefined' && document.cookie) {
      const m = document.cookie.match(/(?:^|; )pacs_override=([^;]*)/);
      if (m) {
        overrideStr = decodeURIComponent(m[1]);
      }
    }
    if (!overrideStr && typeof localStorage !== 'undefined') {
      overrideStr = localStorage.getItem('pacs_override');
    }
    if (!overrideStr && typeof sessionStorage !== 'undefined') {
      overrideStr = sessionStorage.getItem('pacs_override');
    }
    if (overrideStr) {
      const ov = JSON.parse(overrideStr);
      if (ov && ov.aeTitle) {
        ae = ov.aeTitle;
      }
    } else {
      // Fallback to pacsDetails at login
      let detailsStr: string | null = null;
      if (typeof sessionStorage !== 'undefined') {
        detailsStr = sessionStorage.getItem('pacsDetails');
      }
      if (!detailsStr && typeof localStorage !== 'undefined') {
        detailsStr = localStorage.getItem('pacsDetails');
      }
      if (detailsStr) {
        const det = JSON.parse(detailsStr);
        if (det && det.aeTitle) {
          ae = det.aeTitle;
        }
      }
    }
  } catch (e) {}
  return ae;
};
export const getPacsAETitle = () => _computePacsAETitle();

// DICOMweb goes through our backend proxy, which derives the clinic from the signed-in
// user's app token and enforces per-clinic scope server-side. These helpers let the
// custom PACS callers (study counts, wall-motion SR, patient details) use that path too.
export const getDicomWebProxyURL = () => `${baseURL}/dicomweb`;
export const getAppAuthToken = () =>
  sessionStorage.getItem('token') || localStorage.getItem('token') || '';

/**
 * Staging-only study-date ceiling. When a clinic's Pacs doc carries `studyDateMax`
 * (YYYYMMDD) — injected ONLY on staging for demo clinics like Bond Radiology — the
 * study worklist is capped to studies on/before that date, freezing the demo set to a
 * period whose lite images are guaranteed synced. Production Pacs docs never carry the
 * field, so this returns null on prod and the worklist is left untouched.
 */
const _computeStudyDateMax = (): string | null => {
  try {
    let detailsStr: string | null = null;
    if (typeof sessionStorage !== 'undefined') {
      detailsStr = sessionStorage.getItem('pacsDetails');
    }
    if (!detailsStr && typeof localStorage !== 'undefined') {
      detailsStr = localStorage.getItem('pacsDetails');
    }
    if (detailsStr) {
      const det = JSON.parse(detailsStr);
      if (det && det.studyDateMax) {
        const cap = String(det.studyDateMax).replace(/[^0-9]/g, '');
        return cap.length === 8 ? cap : null;
      }
    }
  } catch (e) {}
  return null;
};
export const getStudyDateMax = (): string | null => _computeStudyDateMax();

export const KEYCLOCK_URL = 'https://keycloak.cardiachealth.ai';

export const AUTH = {
  LOGIN_URL: `${baseURL}/auth/login`,
  VERIFY_LOGIN_MFA_URL: `${baseURL}/auth/verify-login-mfa`,
  REFRESH_TOKEN_URL: `${baseURL}/auth/refresh-token`,
  CHANGE_PASSWORD_URL: `${baseURL}/auth/changePassword`,
  FORGOT_PASSWORD_URL: `${baseURL}/auth/forgot-password`,
  RESET_PASSWORD_URL: `${baseURL}/auth/resetPassword`,
  OTP_VERIFICATION_URL: `${baseURL}/auth/getOTPfromDB`,
  PACS_TOKEN_URL: `${baseURL}/auth/pacs-token`,
  MULTIPLE_HOSPITALS: `${baseURL}/auth/fetchRelatedHospitals`,
  GET_USER_DOCUMENTS: `${baseURL}/auth/getUserDocuments`,
};

export const CLINICS = {
  ADD_CLINIC_URL: `${baseURL}/clinic/addClinic`,
  UPDATE_CLINIC_URL: `${baseURL}/clinic/editClinic`,
  GET_CLINIC_URL: `${baseURL}/clinic/getClinic`,
  DELETE_CLINIC_URL: `${baseURL}/clinic/deleteClinic`,
  HOSPITAL_TEMPLATE_URL: `${baseURL}/clinic/hospitaltemplate`,
  GET_HOSPITAL_TEMPLATE_URL: `${baseURL}/clinic/hospitalDetail`,
  UPLOAD_CLINIC_LOGO: `${baseURL}/report/uploadPhoto`,
  ADD_ADMIN_URL: `${baseURL}/clinic/addAdmin`,
  ALERT_SETTINGS_URL: `${baseURL}/clinic/alertSettings`,
  DROPDOWN_SETTINGS_URL: `${baseURL}/clinic/dropdownSettings`,
  // Per-clinic feature control. FEATURES_URL is the caller's own flags (any
  // authenticated user); FEATURE_CONTROL_URL is the super-admin list/update.
  FEATURES_URL: `${baseURL}/clinic/features`,
  FEATURE_CONTROL_URL: `${baseURL}/clinic/featureControl`,
  // Clinic groups for Group Admin users (super-admin only).
  CLINIC_GROUPS_URL: `${baseURL}/clinic/groups`,
  CLINIC_GROUP_URL: (id: string) => `${baseURL}/clinic/groups/${encodeURIComponent(id)}`,
};

export const USERS = {
  ADD_USER_URL: `${baseURL}/user/addUser`,
  GET_USER_URL: `${baseURL}/user/getUser`,
  UPDATE_USER_URL: `${baseURL}/user/updateUser`,
  DELETE_USER_URL: `${baseURL}/user/deleteUser`,
  PREFILL_USER_URL: `${baseURL}/auth/preFillUser`,
  // Cardiologists of the caller's clinic — the only role a report may be assigned to.
  GET_CARDIOLOGISTS: `${baseURL}/user/fetchCardiologists`,
  GET_SONOGRAPHERS: `${baseURL}/user/fetchSonographers`,
  GP_ACCOUNTS: `${baseURL}/user/gp-accounts`,
  GP_ACCOUNT_STATUS: (id: string) => `${baseURL}/user/gp-accounts/${encodeURIComponent(id)}/status`,
  GP_ACCOUNT_RESET_PASSWORD: (id: string) =>
    `${baseURL}/user/gp-accounts/${encodeURIComponent(id)}/reset-password`,
  WEB_PUSH_TOKEN_URL: `${baseURL}/user/webPushToken`,
  WEB_PUSH_TOKEN_REMOVE_URL: `${baseURL}/user/webPushToken/remove`,
};

/** Signed CloudFront access to a study's per-frame JPEG copies (echoFramesLoader). */
export const VIEWER_FRAMES = {
  STUDY_ACCESS: (studyUID: string) => `${baseURL}/viewer-frames/${encodeURIComponent(studyUID)}`,
};

/** Group Admin reads: the clinics of the signed-in user's clinic group. */
export const GROUP_ADMIN = {
  CLINICS: `${baseURL}/group-admin/clinics`,
  DASHBOARD: `${baseURL}/group-admin/dashboard`,
  REPORTS: `${baseURL}/group-admin/reports`,
  REPORT: (id: string) => `${baseURL}/group-admin/reports/${encodeURIComponent(id)}`,
  REPORT_PDF: (id: string) => `${baseURL}/group-admin/reports/${encodeURIComponent(id)}/pdf`,
  REPORT_LITE_IMAGES: (id: string) => `${baseURL}/group-admin/reports/${encodeURIComponent(id)}/lite-images`,
  REPORT_MEASUREMENTS: (id: string) => `${baseURL}/group-admin/reports/${encodeURIComponent(id)}/measurements`,
  REPORT_DISTRIBUTIONS: (id: string) => `${baseURL}/group-admin/reports/${encodeURIComponent(id)}/distributions`,
  STUDIES: `${baseURL}/group-admin/studies`,
  STUDY_LITE_IMAGES: (uid: string) => `${baseURL}/group-admin/studies/${encodeURIComponent(uid)}/lite-images`,
  STUDY_REPORTS: (uid: string) => `${baseURL}/group-admin/studies/${encodeURIComponent(uid)}/reports`,
};

export const PROFILE = {
  GET_PROFILE_DETAILS: `${baseURL}/user/getProfile`,
  UPDATE_PROFILE_DETAILS: `${baseURL}/user/updateProfile`,
};

export const COMMON_DATA = {
  GET_COMMON_DATA: `${baseURL}/common`,
};

export const REPORTS = {
  GET_HOPSPITAL_DOCTORS_LIST: `${baseURL}/user/fetchHospitalDoctors`,
  GET_REPORTS_LIST: `${baseURL}/report/getReports`,
  ASSIGN_REPORT: `${baseURL}/report/assignReport`,
  SET_SONOGRAPHER: `${baseURL}/report/setSonographer`,
  FETCH_DOCTORS_TO_ASSIGN: `${baseURL}/report/fetchDoctorsToAssign`,
  GET_REPORT_TEMPLATE_BY_ID_URL: `${baseURL}/report/getReportTemplateById`,
  GET_DEFAULT_REPORT_TEMPLATE_URL: `${baseURL}/report/getDefaultReportTemplate`,

  CREATE_REPORT_TEMPLATE_URL: `${baseURL}/report/createReportTemplate`,
  GET_REPORT_TEMPLATE_URL: `${baseURL}/report/getReportTemplates`,
  UPDATE_REPORT_TEMPLATE_URL: `${baseURL}/report/updateReportTemplate`,
  DELETE_REPORT_TEMPLATE_URL: `${baseURL}/report/deleteReportTemplate`,

  CREATE_REPORT_URL: `${baseURL}/report/createReport`,
  GET_REPORT_URL: `${baseURL}/report/getReport`,
  GET_REPORT_FROM_PATIENT_ID_URL: `${baseURL}/report/getReportsFromPatientId`,
  UPDATE_REPORT_URL: `${baseURL}/report/updateReport`,
  DELETE_REPORT_URL: `${baseURL}/report/deleteReport`,

  GET_CURRENT_REPORT_URL: `${baseURL}/report/getCurrentReport`,
  GET_ALL_REPORTS_BY_STUDY_INSTANCE_URL: `${baseURL}/report/fetchReportsByStudyInstanceUID`,
  CHANGE_REPORT_STATUS: `${baseURL}/report/changeReportStatus`,
  UPDATE_REPORT: `${baseURL}/report/updateReportTemplateHtml`,
  SAVE_DIAGRAM_HTML: `${baseURL}/report/saveDiagramHtml`,
  GET_LITE_IMAGES: `${baseURL}/report/lite-images`,
  UPDATE_REPORT_CLIPS: (reportId: string) => `${baseURL}/report/${reportId}/clips`,
  GET_PUBLIC_CLIPS: (uuid: string) => `${baseURL}/report/public/clips/${uuid}`,

  // Report labels — receptionist workflow markers ("Uploaded to BP") on the reports list.
  REPORT_LABELS: `${baseURL}/report/labels`,
  REPORT_LABEL_BY_ID: (labelId: string) => `${baseURL}/report/labels/${labelId}`,
  SET_REPORT_LABELS: (reportId: string) => `${baseURL}/report/${reportId}/labels`,
  GET_REPORT_DATA_BY_ID: `${baseURL}/report/getReportById`,

  GET_STUDY_MARKINGS: `${baseURL}/report/getMarkCriticalHandler`,
  ADD_STUDY_MARKINGS: `${baseURL}/report/markCriticalHandler`,
  REMOVE_STUDY_MARKINGS: `${baseURL}/report/deleteMarkCriticalEntries`,

  // Echo measurements (EchoPAC-style structured store) — own endpoints
  SAVE_STRUCTURED_MEASUREMENTS: `${baseURL}/report/measurements`,
  GET_STRUCTURED_MEASUREMENTS: `${baseURL}/report/measurements`,
  // CardioSoft stress values parsed from the study's attached XML (read-only)
  GET_STRESS_VALUES: `${baseURL}/report/stress-values`,

  GET_STUDY_TOPICS: `${baseURL}/report/getTeachingTopics`,
  ADD_STUDY_TOPICS: `${baseURL}/report/addTeachingTopics`,

  DOWNLOAD_PDF: `${baseURL}/report/generatePdfFromTemplate`,
  REGENERATE_REPORT: `${baseURL}/report/regenerate`,
  SEARCH_STUDY_UIDS: `${baseURL}/report/searchStudyUIDs`,

  SEND_REMINDER: `${baseURL}/report/sendReminder`,
  DISMISS_REMINDER: `${baseURL}/report/dismissReminder`,
  SEND_REPORT_NOTIFICATION: `${baseURL}/report/sendReportNotification`,
  GET_NOTIFICATIONS: `${baseURL}/report/notifications`,
  MARK_NOTIFICATION_READ: `${baseURL}/report/notifications/markRead`,
  MARK_ALL_NOTIFICATIONS_READ: `${baseURL}/report/notifications/markAllRead`,
  UPDATE_PATIENT_NAME: `${baseURL}/report/updatePatientName`,
  GET_PATIENT_NAME_OVERRIDES: `${baseURL}/report/getPatientNameOverrides`,
  REPEAT_STUDY_CHECK: `${baseURL}/report/repeatStudyCheck`,
};

export const AUDITLOGS = {
  GET_AUDIT_LOGS: `${baseURL}/report/getAuditLogs`,
};

export const DASHBOARD = {
  GET_DASHBOARD_DATA: `${baseURL}/dashboard/graphData`,
  GET_INSIGHTS: `${baseURL}/dashboard/insights`,
  UPDATE_CLINIC_BILLING: `${baseURL}/dashboard/clinicBilling`,
  SEND_INVOICE: `${baseURL}/dashboard/sendInvoice`,
  INVOICE_PREVIEW: `${baseURL}/dashboard/invoicePreview`,
  INVOICES: `${baseURL}/dashboard/invoices`,
  INVOICE_GENERATE_PREVIEW: `${baseURL}/dashboard/invoices/generate-preview`,
  INVOICE_PAYMENT: `${baseURL}/dashboard/invoices/payment`,
  INVOICE_VOID: `${baseURL}/dashboard/invoices/void`,
  INVOICE_RESEND: `${baseURL}/dashboard/invoices/resend`,
  INVOICE_REMINDER: `${baseURL}/dashboard/invoices/reminder`,
  INVOICE_EXPORT: `${baseURL}/dashboard/invoices/export`,
  INVOICE_GENERATE: `${baseURL}/dashboard/invoices/generate`,
};

export const _pacsUrls = {
  GET_PACS: `${baseURL}/pacs/fetchPacs`,
  CREATE_PACS: `${baseURL}/pacs/create`,
  EDIT_PACS: `${baseURL}/pacs/edit`,
  DELETE_PACS: `${baseURL}/pacs/delete`,
};

export const RELATED_REPORT = {
  UPLOAD_PATIENT_PRESCRIPTION: `${baseURL}/clinic/uploadPrescription`,
  GET_PATIENT_PRESCRIPTION: `${baseURL}/clinic/patientPrescription`,
  DELETE_PATIENT_PRESCRIPTION: `${baseURL}/clinic/deletePrescription`,
};

export const _expressUrls = {
  GET_IMAGE_URLS: `${baseURL}/liteimg/getLiteDcmImgs`,
  GET_CLIP_SETTINGS: `${baseURL}/clip/getClipSettings`,
  SAVE_CLIP_SETTING: `${baseURL}/clip/saveClipSetting`,
};

export const hl7BaseURL =
  process.env.REACT_APP_BACKEND_URL || 'https://dicom-echo-backend.cardiachealth.ai/v2';

export const HL7 = {
  SEND: `${hl7BaseURL}/hl7/send`,
  PROVIDER_LOCAL: `${hl7BaseURL}/hl7/provider/local`,
  PROVIDER_LOOKUP_BYNAME: `${hl7BaseURL}/hl7/provider/lookup/byname`,
  PROVIDER_LOOKUP_BYID: `${hl7BaseURL}/hl7/provider/lookup/byid`,
  PROVIDER_IS_ROUTABLE: `${hl7BaseURL}/hl7/provider/isRoutable`,
  GET_CURRENT_REPORT: `${hl7BaseURL}/report/getCurrentReport`,
  HISTORY: (reportId: string) => `${hl7BaseURL}/hl7/history/${reportId}`,
  HISTORY_BY_STUDY: (studyInstanceUID: string) =>
    `${hl7BaseURL}/hl7/history/by-study/${encodeURIComponent(studyInstanceUID)}`,
  DISTRIBUTION_COUNTS: `${hl7BaseURL}/hl7/distribution/counts`,
  // ?download=1 sends it as an attachment; without it the browser opens it inline.
  RTF: (reportId: string) => `${hl7BaseURL}/hl7/preview-rtf/${reportId}`,
  CLINIC_FROM_PROVIDERS: (reportId: string) =>
    `${hl7BaseURL}/hl7/clinic-from-providers?reportId=${encodeURIComponent(reportId)}`,
  SAVE_DRAFT: `${hl7BaseURL}/hl7/distribution/save`,
  SEND_DRAFT: (distributionId: string) =>
    `${hl7BaseURL}/hl7/distribution/${encodeURIComponent(distributionId)}/send`,
  DELETE_DRAFT: (distributionId: string) =>
    `${hl7BaseURL}/hl7/distribution/${encodeURIComponent(distributionId)}`,
};

/**
 * Email distribution — sending the report PDF to a typed address.
 *
 * Separate from HL7 above because nothing on this path builds an HL7 message. The
 * history, counts and draft-send endpoints stay under /hl7 and serve both channels, so
 * one list shows every distribution regardless of how it went out.
 */
export const DISTRIBUTION = {
  EMAIL_SAVE_DRAFT: `${hl7BaseURL}/distribution/email/draft`,
  EMAIL_SEND: `${hl7BaseURL}/distribution/email/send`,
};

